import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import { User, Envelope, Lock, Phone, MapPin, Sparkle, ArrowRight, CheckCircle, At, XCircle, CircleNotch } from '@phosphor-icons/react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const AuthPage = () => {
  const [isLogin, setIsLogin] = useState(true); // Default to login form
  const [loading, setLoading] = useState(false);
  const [profileIdStatus, setProfileIdStatus] = useState({ checking: false, available: null, message: '' });
  const { login, signup } = useAuth();
  const navigate = useNavigate();

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    mobile: '',
    location: '',
    profile_id: '',
  });

  // Debounced profile ID check
  useEffect(() => {
    if (!formData.profile_id || formData.profile_id.length < 3) {
      setProfileIdStatus({ checking: false, available: null, message: '' });
      return;
    }

    const timeoutId = setTimeout(async () => {
      setProfileIdStatus({ checking: true, available: null, message: 'Checking...' });
      try {
        const response = await axios.get(`${API_URL}/api/auth/check-profile-id/${formData.profile_id}`);
        setProfileIdStatus({
          checking: false,
          available: response.data.available,
          message: response.data.message
        });
      } catch (error) {
        setProfileIdStatus({ checking: false, available: false, message: 'Error checking availability' });
      }
    }, 500);

    return () => clearTimeout(timeoutId);
  }, [formData.profile_id]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      if (isLogin) {
        await login(formData.email, formData.password);
        toast.success('Welcome back!');
      } else {
        if (!profileIdStatus.available) {
          toast.error('Please choose an available Profile ID');
          setLoading(false);
          return;
        }
        await signup(formData.name, formData.email, formData.password, formData.mobile, formData.location, formData.profile_id);
        toast.success('Account created successfully! 🎉');
      }
      navigate('/dashboard');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Authentication failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-purple-900 to-orange-900 flex items-center justify-center p-4 relative overflow-hidden">
      {/* Animated background */}
      <div className="absolute inset-0 opacity-20">
        <div className="absolute top-0 left-0 w-full h-full bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHZpZXdCb3g9IjAgMCA2MCA2MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZyBmaWxsPSJub25lIiBmaWxsLXJ1bGU9ImV2ZW5vZGQiPjxnIGZpbGw9IiNmZmZmZmYiIGZpbGwtb3BhY2l0eT0iMC4xIj48cGF0aCBkPSJNMzYgMzRjMC0yLjIxLTEuNzktNC00LTRzLTQgMS43OS00IDQgMS43OSA0IDQgNCA0LTEuNzkgNC00em0wLTMwYzAtMi4yMS0xLjc5LTQtNC00cy00IDEuNzktNCA0IDEuNzkgNCA0IDQgNC0xLjc5IDQtNHpNNiAzNGMwLTIuMjEtMS43OS00LTQtNHMtNCAxLjc5LTQgNCAxLjc5IDQgNCA0IDQtMS43OSA0LTR6Ii8+PC9nPjwvZz48L3N2Zz4=')] opacity-40"></div>
      </div>

      {/* Floating blobs */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-20 left-10 w-72 h-72 bg-orange-500 rounded-full mix-blend-multiply filter blur-3xl opacity-20 animate-blob"></div>
        <div className="absolute top-40 right-10 w-72 h-72 bg-purple-500 rounded-full mix-blend-multiply filter blur-3xl opacity-20 animate-blob animation-delay-2000"></div>
        <div className="absolute bottom-20 left-1/3 w-72 h-72 bg-pink-500 rounded-full mix-blend-multiply filter blur-3xl opacity-20 animate-blob animation-delay-4000"></div>
      </div>

      <div className="w-full max-w-6xl relative z-10">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-center">
          
          {/* Left Side - Branding */}
          <div className="text-left text-white space-y-6 px-4 animate-fade-in-left hidden lg:block">
            <Badge className="bg-gradient-to-r from-orange-500 to-pink-500 text-white border-0 mb-4 px-5 py-2.5 text-sm font-bold shadow-lg inline-flex items-center gap-2 animate-bounce-slow">
              <Sparkle className="h-4 w-4 animate-pulse" weight="fill" /> 
              Designbiz.in
            </Badge>
            
            <h1 className="font-['Chivo'] text-4xl md:text-5xl lg:text-6xl font-black tracking-tighter leading-tight">
              तुमची{" "}
              <span className="bg-gradient-to-r from-orange-400 to-pink-500 bg-clip-text text-transparent">
                Profile बनवा,
              </span>
              <br />
              आणि Business ला
              <br />
              Grow करा!
            </h1>

            <p className="text-xl text-gray-300 leading-relaxed">
              तुमचा संपूर्ण business एकाच ठिकाणी व्यवस्थित manage करा
            </p>

            <div className="space-y-4 pt-6">
              {[
                'Enquiry Management & Tracking',
                'Professional Invoices & Payments',
                'Business Analytics Dashboard',
                'WhatsApp Integration'
              ].map((feature, i) => (
                <div key={i} className="flex items-center gap-3 bg-white/10 backdrop-blur-sm px-4 py-3 rounded-lg border border-white/20">
                  <CheckCircle className="h-6 w-6 text-green-400 flex-shrink-0" weight="fill" />
                  <span className="font-medium">{feature}</span>
                </div>
              ))}
            </div>

            <div className="grid grid-cols-3 gap-4 pt-8 border-t border-white/20">
              <div>
                <div className="font-['Chivo'] text-3xl font-black text-orange-400">500+</div>
                <div className="text-sm text-gray-400">Designers</div>
              </div>
              <div>
                <div className="font-['Chivo'] text-3xl font-black text-purple-400">₹2 Cr+</div>
                <div className="text-sm text-gray-400">Revenue</div>
              </div>
              <div>
                <div className="font-['Chivo'] text-3xl font-black text-green-400">98%</div>
                <div className="text-sm text-gray-400">Happy</div>
              </div>
            </div>
          </div>

          {/* Right Side - Auth Form */}
          <div className="animate-fade-in-right">
            <Card className="border-2 border-orange-500 bg-white shadow-2xl relative overflow-hidden">
              <div className="absolute inset-0 bg-gradient-to-br from-orange-500/5 to-purple-500/5"></div>
              
              <CardContent className="relative pt-8 pb-8 px-6 md:px-10">
                {/* Mobile Logo */}
                <div className="text-center mb-6 lg:hidden">
                  <h2 className="font-['Chivo'] text-2xl font-black text-orange-600 mb-2">
                    Designbiz.in
                  </h2>
                  <p className="text-sm text-gray-600">Complete Business Solution</p>
                </div>

                <div className="text-center mb-8">
                  <div className="flex items-center justify-center gap-2 mb-4">
                    <button
                      type="button"
                      onClick={() => setIsLogin(false)}
                      className={`px-6 py-2.5 rounded-full font-bold transition-all ${
                        !isLogin 
                          ? 'bg-gradient-to-r from-orange-500 to-pink-600 text-white shadow-lg' 
                          : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                      }`}
                      data-testid="signup-tab-button"
                    >
                      Sign Up
                    </button>
                    <button
                      type="button"
                      onClick={() => setIsLogin(true)}
                      className={`px-6 py-2.5 rounded-full font-bold transition-all ${
                        isLogin 
                          ? 'bg-gradient-to-r from-orange-500 to-pink-600 text-white shadow-lg' 
                          : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                      }`}
                      data-testid="login-tab-button"
                    >
                      Login
                    </button>
                  </div>

                  <h3 className="font-['Chivo'] text-2xl md:text-3xl font-black text-gray-900 mb-2">
                    {isLogin ? 'Welcome Back!' : '🎉 Free Profile बनवा!'}
                  </h3>
                  <p className="text-gray-600 text-sm">
                    {isLogin ? 'Sign in to your account' : 'Join 500+ designers growing their business'}
                  </p>
                </div>

                <form onSubmit={handleSubmit} className="space-y-4">
                  {!isLogin && (
                    <>
                      <div className="space-y-2">
                        <Label htmlFor="name" className="text-sm font-semibold text-gray-900">
                          तुमचं नाव *
                        </Label>
                        <div className="relative">
                          <User className="absolute left-3 top-3.5 h-5 w-5 text-gray-400" weight="duotone" />
                          <Input
                            id="name"
                            data-testid="signup-name-input"
                            placeholder="उदा: प्रशांत देशमुख"
                            value={formData.name}
                            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                            className="pl-11 h-12 border-2 border-gray-200 focus:border-orange-500 transition-all"
                            required
                          />
                        </div>
                      </div>

                      {/* Profile ID Field - Instagram/YouTube style username */}
                      <div className="space-y-2">
                        <Label htmlFor="profile_id" className="text-sm font-semibold text-gray-900">
                          Profile ID (Username) * <span className="text-xs text-gray-500">- तुमची unique link</span>
                        </Label>
                        <div className="relative">
                          <At className="absolute left-3 top-3.5 h-5 w-5 text-gray-400" weight="bold" />
                          <Input
                            id="profile_id"
                            data-testid="signup-profile-id-input"
                            placeholder="prashant_designs"
                            value={formData.profile_id}
                            onChange={(e) => setFormData({ ...formData, profile_id: e.target.value.toLowerCase().replace(/[^a-z0-9_.]/g, '') })}
                            className={`pl-11 pr-10 h-12 border-2 transition-all ${
                              profileIdStatus.available === true ? 'border-green-500 focus:border-green-500' :
                              profileIdStatus.available === false ? 'border-red-500 focus:border-red-500' :
                              'border-gray-200 focus:border-orange-500'
                            }`}
                            required
                          />
                          <div className="absolute right-3 top-3.5">
                            {profileIdStatus.checking && (
                              <CircleNotch className="h-5 w-5 text-gray-400 animate-spin" weight="bold" />
                            )}
                            {!profileIdStatus.checking && profileIdStatus.available === true && (
                              <CheckCircle className="h-5 w-5 text-green-500" weight="fill" />
                            )}
                            {!profileIdStatus.checking && profileIdStatus.available === false && (
                              <XCircle className="h-5 w-5 text-red-500" weight="fill" />
                            )}
                          </div>
                        </div>
                        {formData.profile_id && (
                          <div className="flex items-center justify-between">
                            <p className={`text-xs ${profileIdStatus.available ? 'text-green-600' : profileIdStatus.available === false ? 'text-red-600' : 'text-gray-500'}`}>
                              {profileIdStatus.message || 'Min 3 characters'}
                            </p>
                            {formData.profile_id.length >= 3 && (
                              <p className="text-xs text-gray-400">
                                Link: designbiz.in/<span className="font-bold text-orange-600">{formData.profile_id}</span>
                              </p>
                            )}
                          </div>
                        )}
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label htmlFor="mobile" className="text-sm font-semibold text-gray-900">
                            Mobile Number *
                          </Label>
                          <div className="relative">
                            <Phone className="absolute left-3 top-3.5 h-5 w-5 text-gray-400" weight="duotone" />
                            <Input
                              id="mobile"
                              data-testid="signup-mobile-input"
                              placeholder="9876543210"
                              value={formData.mobile}
                              onChange={(e) => setFormData({ ...formData, mobile: e.target.value })}
                              className="pl-11 h-12 border-2 border-gray-200 focus:border-orange-500 transition-all"
                              required
                            />
                          </div>
                        </div>

                        <div className="space-y-2">
                          <Label htmlFor="location" className="text-sm font-semibold text-gray-900">
                            Location (Optional)
                          </Label>
                          <div className="relative">
                            <MapPin className="absolute left-3 top-3.5 h-5 w-5 text-gray-400" weight="duotone" />
                            <Input
                              id="location"
                              data-testid="signup-location-input"
                              placeholder="Mumbai"
                              value={formData.location}
                              onChange={(e) => setFormData({ ...formData, location: e.target.value })}
                              className="pl-11 h-12 border-2 border-gray-200 focus:border-orange-500 transition-all"
                            />
                          </div>
                        </div>
                      </div>
                    </>
                  )}

                  <div className="space-y-2">
                    <Label htmlFor="email" className="text-sm font-semibold text-gray-900">
                      Email *
                    </Label>
                    <div className="relative">
                      <Envelope className="absolute left-3 top-3.5 h-5 w-5 text-gray-400" weight="duotone" />
                      <Input
                        id="email"
                        data-testid={isLogin ? "login-email-input" : "signup-email-input"}
                        type="email"
                        placeholder="your@email.com"
                        value={formData.email}
                        onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                        className="pl-11 h-12 border-2 border-gray-200 focus:border-orange-500 transition-all"
                        required
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <Label htmlFor="password" className="text-sm font-semibold text-gray-900">
                        Password *
                      </Label>
                      {isLogin && (
                        <Link 
                          to="/forgot-password" 
                          className="text-sm text-orange-600 hover:text-orange-700 font-medium hover:underline"
                          data-testid="forgot-password-link"
                        >
                          Password विसरलात?
                        </Link>
                      )}
                    </div>
                    <div className="relative">
                      <Lock className="absolute left-3 top-3.5 h-5 w-5 text-gray-400" weight="duotone" />
                      <Input
                        id="password"
                        data-testid={isLogin ? "login-password-input" : "signup-password-input"}
                        type="password"
                        placeholder="••••••••"
                        value={formData.password}
                        onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                        className="pl-11 h-12 border-2 border-gray-200 focus:border-orange-500 transition-all"
                        required
                      />
                    </div>
                  </div>

                  <Button
                    type="submit"
                    data-testid={isLogin ? "login-submit-button" : "signup-submit-button"}
                    className="w-full bg-gradient-to-r from-orange-500 to-pink-600 hover:from-orange-600 hover:to-pink-700 text-white font-bold rounded-full h-12 text-base shadow-xl hover:shadow-2xl transition-all hover:scale-105"
                    disabled={loading}
                  >
                    {loading ? 'Please wait...' : (
                      <span className="flex items-center justify-center gap-2">
                        {isLogin ? 'Sign In' : '🎁 Create Free Profile'}
                        <ArrowRight className="h-5 w-5" weight="bold" />
                      </span>
                    )}
                  </Button>

                  {!isLogin && (
                    <div className="flex flex-wrap items-center justify-center gap-3 text-xs text-gray-600 pt-2">
                      <span className="flex items-center gap-1">
                        <CheckCircle className="h-4 w-4 text-green-600" weight="fill" />
                        100% Free
                      </span>
                      <span className="flex items-center gap-1">
                        <CheckCircle className="h-4 w-4 text-green-600" weight="fill" />
                        No Credit Card
                      </span>
                      <span className="flex items-center gap-1">
                        <CheckCircle className="h-4 w-4 text-green-600" weight="fill" />
                        2 Min Setup
                      </span>
                    </div>
                  )}
                </form>

                <div className="mt-6 text-center">
                  <p className="text-sm text-gray-600">
                    {isLogin ? "Don't have an account? " : 'Already have an account? '}
                    <button
                      data-testid="toggle-auth-mode-button"
                      onClick={() => setIsLogin(!isLogin)}
                      className="text-orange-600 hover:text-orange-700 font-bold hover:underline"
                    >
                      {isLogin ? 'Sign up for free' : 'Sign in'}
                    </button>
                  </p>
                </div>
              </CardContent>
            </Card>
          </div>

        </div>
      </div>
    </div>
  );
};

export default AuthPage;
